// src/api/index.js
import queryString from "query-string";

import { jwt } from "../utils/index.js";

import rootApi from "./api-config.js";

const { REACT_APP_MAIN_SERVER_URL } = process.env;
const API_URL = `${REACT_APP_MAIN_SERVER_URL}/api`;

const api = {
	get: (path, searchParams) => rootApi.get(path, { searchParams: queryString.stringify(searchParams) }).json(),
	post: (path, json) => rootApi.post(path, { json }).json(),
	put: (path, json) => rootApi.put(path, { json }).json(),
	patch: (path, json) => rootApi.patch(path, { json }).json(),
	delete: (path, json) => rootApi.delete(path, { json }).json(),
};

export default api;

// Stream event types: what ecoready-ai-assistant/backend/chat_routes.py (/chat/stream) emits. Must stay in sync with it.
export const STREAM_TYPES = {
	CONVERSATION_ID: "conversation_id",
	THINKING: "thinking",
	CONTENT: "content",
	TOOL_START: "tool_start",
	TOOL_CALL: "tool_call",
	PAUSED: "paused",
	DONE: "done",
	ERROR: "error",
};

export const authenticate = (username, password) => api.post("authenticate", { username, password });
export const authenticateGoogle = (token) => api.post("authenticateGoogle", { token });
export const forgotPassword = (username) => api.post("forgotPassword", { username });
export const resetPassword = (password, token) => api.post("resetPassword", { password, token });
export const signUp = (username, email, password) => api.post("createUser", { username, email, password });
export const invitedSignUp = (username, email, password, token) => api.post("createUserInvited", { username, email, password, token });
// Bug report / question for the project team. Resolves { success: true }, or { status: 400, message } when validation fails;
// a 429 (more than 5 reports in an hour) rejects with a ky HTTPError.
export const sendFeedback = (payload) => api.post("feedback", payload);
export const getFile = async (organization, saveName, originalName) => {
	try {
		const fileName = originalName || saveName;
		// Make the API call to get the file as a blob
		const response = await rootApi.get(
			`file/download/${encodeURIComponent(organization)}/${encodeURIComponent(saveName)}/${encodeURIComponent(fileName)}`,
		);
		const blob = await response.blob();

		// Create a download link and trigger it
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName;
		document.body.append(a);
		a.click();

		// Cleanup
		a.remove();
		window.URL.revokeObjectURL(url);

		return { success: true };
	} catch (error) {
		console.error("Error downloading file:", error);
		throw error;
	}
};

export const inviteUser = (email) => api.post("user", { email });
export const removeUser = (id) => api.post("user/delete", { id });
export const getUsersData = () => api.get("user");

// EcoReadyServices

export const getCollectionData = (organization, project, collection, params) => api.get("eco-ready-services/getdata", { organization, project, collection, params });
export const getCollectionDataStatistics = (organization, project, collection, params) => api.get("eco-ready-services/getdatastatistics", { organization, project, collection, params });

// ============================================================================
// AI Assistant Backend API (proxied through dashboard backend)
// Uses same authentication token from cookies
// ============================================================================

// AI Assistant endpoints are proxied through the main dashboard backend
// This simplifies authentication and CORS handling

// Check if AI Assistant functionality is enabled (uses health endpoint)
export const checkAiEnabled = async () => {
	try {
		const response = await rootApi.get("ai-assistant/health").json();
		return response.status !== "disabled";
	} catch (error) {
		console.warn("AI Assistant health check failed:", error.message);
		return false;
	}
};

// AI Assistant Conversations API
export const getAiConversations = async (limit = 50, skip = 0) => {
	const searchParams = { limit, skip };
	const response = await rootApi.get("ai-assistant/conversations", { searchParams: queryString.stringify(searchParams) }).json();
	return { success: true, conversations: response.conversations || [] };
};

export const getAiConversation = async (id) => {
	const response = await rootApi.get(`ai-assistant/conversations/${id}`).json();
	return { success: true, ...response };
};

export const updateAiConversation = async (id, title) => {
	await rootApi.put(`ai-assistant/conversations/${id}`, { json: { title } });
	return { success: true };
};

export const deleteAiConversation = async (id) => {
	await rootApi.delete(`ai-assistant/conversations/${id}`);
	return { success: true };
};

// Model slots behind the options of the assistant picker: { default, models: [{ id, label, available }] }
export const getAiModels = async () => rootApi.get("ai-assistant/models").json();

// AI Assistant Streaming Chat (proxied through dashboard backend)
// options.mode: "wise" | "bare"; options.model: "primary" | "secondary". Both are fixed per conversation.
// options.turn: "report" asks for a professional report of the conversation; the message text is then only the user's optional brief (length, focus, audience), "" for none.
export const sendAiMessageStream = async (conversationId, message, signal, { mode = "wise", model, turn } = {}) => {
	const token = jwt.getToken();
	return fetch(`${API_URL}/ai-assistant/chat/stream`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"x-access-token": token,
		},
		body: JSON.stringify({
			message,
			conversation_id: conversationId,
			mode,
			...(model && { model }),
			...(turn && { turn }),
		}),
		signal,
	});
};

// Error of a failed report upload: `code` is the `error` of the response body (or "network", "http_error", "bad_response"),
// `status` the HTTP status (0 when there was no response), `serverMessage` the English `message` of the body when it has one
// (the chat shows it for a code it has no text of its own for) and the other fields of the body (`limit`, `chars`) are kept.
const uploadError = (code, status, body, cause) => {
	const extra = {};
	for (const [key, value] of Object.entries(body && typeof body === "object" ? body : {})) {
		if (!["error", "message", "name", "stack", "cause", "code", "status", "serverMessage"].includes(key)) extra[key] = value;
	}

	const serverMessage = typeof body?.message === "string" ? body.message.trim() : "";
	return Object.assign(
		new Error(serverMessage || `Report upload failed (${code})`, cause ? { cause } : undefined),
		extra,
		{ code, status, serverMessage },
	);
};

// Upload a report (PDF, TXT, MD, DOC, DOCX) into a conversation: the backend reads the text and stores it as a user record
// (the file line) and an assistant record (kind "report", origin "upload"); no model is called.
// Resolves the 200 body { conversation_id, new_conversation, title, mode, model, user_message, report_message }; rejects with
// an Error carrying `code`, `status` and the extra fields of the error body (see uploadError). An aborted `signal` rejects
// with the AbortError itself.
// Plain fetch with FormData, like the chat calls: the browser has to set the multipart boundary itself (no Content-Type
// header), and the ky instance would retry a 413 and redirect on a 404.
export const uploadAiReport = async (file, { conversationId, mode, model, signal } = {}) => {
	const token = jwt.getToken();
	// The text fields go before the file, so a streaming multipart parser has them when the document starts
	const form = new FormData();
	if (conversationId) form.append("conversation_id", conversationId);
	if (mode) form.append("mode", mode);
	if (model) form.append("model", model);
	form.append("document", file, file?.name);

	let response;
	try {
		response = await fetch(`${API_URL}/ai-assistant/reports`, {
			method: "POST",
			headers: token ? { "x-access-token": token } : {},
			body: form,
			signal,
		});
	} catch (error) {
		if (error?.name === "AbortError") throw error;
		throw uploadError("network", 0, null, error);
	}

	const body = await response.json().catch(() => null);
	if (!response.ok) {
		throw uploadError(typeof body?.error === "string" && body.error ? body.error : "http_error", response.status, body);
	}

	if (!body?.conversation_id || !body.user_message || !body.report_message) throw uploadError("bad_response", response.status, null);
	return body;
};

// AI Assistant Stop Streaming - stops the backend from receiving more LLM chunks and saves partial response
export const stopAiStream = async (conversationId) => {
	const token = jwt.getToken();
	try {
		const response = await fetch(`${API_URL}/ai-assistant/chat/stop`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"x-access-token": token,
			},
			body: JSON.stringify({
				conversation_id: conversationId,
			}),
		});
		return await response.json();
	} catch (error) {
		console.error("Error stopping AI stream:", error);
		return { success: false, error: error.message };
	}
};

// Start the evaluation of a finished report. Resolves { status, body }: 202 { status: "running", run_id, started_at };
// 409 { error: "evaluation_exists", report_evaluation } when a run already owns the report; 400/404 { error, message }.
// Raw fetch, like the chat calls: the ky instance redirects to the not-found page on any 404, and the caller needs the
// body of every status.
export const startReportEvaluation = async (conversationId, messageId) => {
	const token = jwt.getToken();
	const response = await fetch(
		`${API_URL}/ai-assistant/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/evaluation`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"x-access-token": token,
			},
			body: "{}",
		},
	);
	const body = await response.json().catch(() => null);
	return { status: response.status, body };
};

